import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import get from 'lodash/get';
import requestAnimationFrame from 'raf';
import random from 'lodash/random';

// How many on screen at a time.
var LIMIT = 16;

// Physics shit
var FRICTION = 0.95;
var GRAVITY = 0.025;

// Velocity ranges
var VELOCITY_RANGE_X = [-6.8, 6.8];
var VELOCITY_RANGE_Y = [-6.4, -4.8];

// Particle size range
var SIZE_RANGE = [3, 8];

// How wide should the angle be
var PARTICLE_ANGLE = 0.8;
var ROTATION_SPEED_RANGE = [0.02, 0.17];

// Fading
var FADE_IN_SPEED = 0.18;
var FADE_OUT_SPEED = 0.04;

// At what point should we start fading out (max 1.0)
// Higher = start fading closer to the bottom
// Lower = start fading early
var FADE_Y_START = 0.45;

// What colors to use.
var COLORS = ['#01e8ff', '#ff4186', '#ffe901', '#47DE91'];

// Device pixel ratio, need to scale the canvas so the
// shapes don't like like rubbish on Retina displays.
var DPR = window.devicePixelRatio || 1;

/**
 * Creates a confetti renderer.
 *
 * @param {*} canvas
 */
export function createConfetti(canvas) {
  // Drawing context.
  var ctx = canvas.getContext('2d');

  // Particle generator.
  var generator = createParticleGenerator();

  // If using delayed start, this is the timer ID.
  var delayedStartTimer = null;

  // Tracks whether the animation is running.
  // If not, no animation frames are requested.
  var running = false;

  // Track the canvas width and height so we don't need to access it every time.
  var canvasWidth = 0;
  var canvasHeight = 0;

  // Configures defaults.
  configure();
  return {
    configure: configure,
    start: start,
    stop: stop
  };

  /**
   * Configures the particle generator.
   * @param {*} props
   */
  function configure() {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    // Account for High DPI displays (Retina)
    var rect = canvas.getBoundingClientRect();
    canvasWidth = rect.width * DPR;
    canvasHeight = rect.height * DPR;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    ctx.scale(DPR, DPR);
    generator.configure(_objectSpread(_objectSpread({}, props), {}, {
      viewportWidth: rect.width,
      viewportHeight: rect.height
    }));
  }

  /**
   * Starts the animation.
   *
   * @param {number | undefined} delay
   */
  function start(delay) {
    running = true;
    if (delay) {
      delayedStartTimer = setTimeout(animate, delay);
    } else {
      requestAnimationFrame(animate);
    }
  }

  /**
   * Stops the animation.
   */
  function stop() {
    running = false;
    clearTimeout(delayedStartTimer);
  }

  /**
   * Animation loop.
   */
  function animate() {
    if (!running) {
      return;
    }
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    generator.render(ctx);
    requestAnimationFrame(animate);
  }
}

/**
 * Particle generator
 */
function createParticleGenerator() {
  var viewportWidth = 0;
  var viewportHeight = 0;
  var limit = LIMIT;
  var friction = FRICTION;
  var gravity = GRAVITY;
  var velocityRangeX = VELOCITY_RANGE_X;
  var velocityRangeY = VELOCITY_RANGE_Y;
  var sizeRange = SIZE_RANGE;
  var colorIdx = 0;
  var particles = [];

  /**
   * Area where confetti can spawn.
   */
  var confettiSource;
  return {
    configure: configure,
    render: render
  };

  /**
   * Configures the generator props.
   *
   * @param {*} props
   */
  function configure(props) {
    // Dereferencing like this to avoid having to `props.lolz`.
    limit = get(props, 'limit', limit);
    friction = get(props, 'friction', friction);
    gravity = get(props, 'gravity', gravity);
    viewportHeight = get(props, 'viewportHeight', viewportHeight);
    viewportWidth = get(props, 'viewportWidth', viewportWidth);
    velocityRangeX = get(props, 'velocityRangeX', velocityRangeX);
    velocityRangeY = get(props, 'velocityRangeY', velocityRangeY);
    sizeRange = get(props, 'sizeRange', sizeRange);
    if (!confettiSource) {
      confettiSource = {
        width: viewportWidth,
        height: 0,
        x: 0,
        y: 0
      };
    }
    confettiSource = _objectSpread(_objectSpread({}, confettiSource), props.confettiSource);
  }

  /**
   * Renders all particles and tries to add one.
   *
   * @param {CanvasRenderingContext2D} ctx
   */
  function render(ctx) {
    maybeAddParticle();
    var len = particles.length;
    for (var i = 0; i < len; i++) {
      var particle = particles[i];
      particle.render(ctx);
      if (particle.shouldBeRemoved()) {
        // Since we are removing in-place, need to go back one and also adjust the length-
        particles.splice(i--, 1);
        len--;
      }
    }
  }

  /**
   * Adds a particle if there's room.
   */
  function maybeAddParticle() {
    var count = particles.length;
    if (count < limit) {
      var newParticleX = random(confettiSource.x, confettiSource.width + confettiSource.x);
      var newParticleY = random(confettiSource.y, confettiSource.height + confettiSource.y);
      particles.push(createParticle(newParticleX, newParticleY));
    }
  }

  /**
   * Creates a particle starting at the specified coords.
   *
   * @param {number} x
   * @param {number} y
   */
  function createParticle(x, y) {
    var color = nextColor();
    var size = random(sizeRange[0], sizeRange[1]);
    var force = size * gravity;
    var angle = random(0, Math.PI, true);
    var angleStart = angle * Math.PI;
    var angleEnd = (angle + PARTICLE_ANGLE) * Math.PI;
    var arcX = size / 2;
    var arcY = size / 2;
    var rotationSpeed = random(ROTATION_SPEED_RANGE[0], ROTATION_SPEED_RANGE[1]);
    var velocityX = random(velocityRangeX[0], velocityRangeX[1]);
    var velocityY = random(velocityRangeY[0], velocityRangeY[1]);
    var rotationX = 0;
    var fadingOut = false;
    var opacity = 0.0;
    return {
      render: render,
      shouldBeRemoved: shouldBeRemoved
    };

    /**
     * Updates the particle's state.
     */
    function update() {
      x += velocityX;
      y += velocityY;
      // Apply force.
      velocityY += force;
      // Apply friction
      velocityX *= friction;
      velocityY *= friction;
      // Apply spin
      rotationX += rotationSpeed;
      if (!fadingOut) {
        opacity = Math.min(opacity + FADE_IN_SPEED, 1);
        if (y / viewportHeight > FADE_Y_START) {
          fadingOut = true;
        }
      } else {
        opacity = Math.max(opacity - FADE_OUT_SPEED, 0);
      }
    }

    /**
     * Draws the particle.
     *
     * @param {CanvasRenderingContext2D} ctx
     */
    function render(ctx) {
      // Render the particle.
      if (opacity > 0) {
        ctx.save();
        ctx.translate(x, y);
        ctx.beginPath();
        ctx.rotate(rotationX);
        ctx.globalAlpha = opacity;
        ctx.lineWidth = size / 1.25;
        ctx.strokeStyle = color;
        // I can't even explain why this works. ¯\_(ツ)_/¯
        ctx.arc(arcX, arcY, size, angleStart, angleEnd);
        ctx.stroke();
        ctx.restore();
      }

      // Updates the state
      update();
    }

    /**
     * Determines whether the particle is ready for removal.
     */
    function shouldBeRemoved() {
      return fadingOut && opacity <= 0;
    }

    /**
     * Gets the next color to use.
     */
    function nextColor() {
      var color = COLORS[colorIdx];
      colorIdx = COLORS.length === colorIdx + 1 ? 0 : colorIdx + 1;
      return color;
    }
  }
}
//# sourceMappingURL=confetti.js.map
